import { useState, useEffect } from 'react'
import { Form, Button, Row, Col } from 'react-bootstrap'
import { useQuery } from 'react-query'
import api from '../../services/api'

const AccountForm = ({ account, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    account_type: 'ACTIVO',
    sat_code: '',
    parent: null,
    is_active: true,
    is_deductible: false,
    description: '',
    orden: 0
  })

  const [errors, setErrors] = useState({})

  // Obtener cuentas para seleccionar como padre
  const { data: accounts } = useQuery(
    'accounts-for-parent',
    async () => {
      const response = await api.get('/accounting/accounts/?is_active=true')
      return response.data.results || response.data || []
    }
  )

  useEffect(() => {
    if (account) {
      setFormData({
        code: account.code || '',
        name: account.name || '',
        account_type: account.account_type || 'ACTIVO',
        sat_code: account.sat_code || '',
        parent: account.parent || account.parent_id || null,
        is_active: account.is_active !== undefined ? account.is_active : true,
        is_deductible: account.is_deductible || false,
        description: account.description || '',
        orden: account.orden || 0
      })
    }
  }, [account])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const newErrors = {}
    if (!formData.code) newErrors.code = 'Código es requerido'
    if (!formData.name) newErrors.name = 'Nombre es requerido'
    if (!formData.account_type) newErrors.account_type = 'Tipo de cuenta es requerido'
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    
    const submitData = { ...formData }
    if (!submitData.parent) {
      submitData.parent = null
    }
    
    if (account && account.id) {
      submitData.id = account.id
    }
    
    onSubmit(submitData)
  }

  // Filtrar cuentas para evitar ciclos (no permitir seleccionar la cuenta actual como padre)
  const availableParents = accounts?.filter(acc => 
    !account?.id || acc.id !== account.id
  ) || []

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Código *</Form.Label>
            <Form.Control
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              isInvalid={!!errors.code}
              required
              placeholder="Ej: 1.1.1"
            />
            {errors.code && <Form.Control.Feedback type="invalid">{errors.code}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Nombre *</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              isInvalid={!!errors.name}
              required
            />
            {errors.name && <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Tipo de Cuenta *</Form.Label>
            <Form.Select
              name="account_type"
              value={formData.account_type}
              onChange={handleChange}
              isInvalid={!!errors.account_type}
              required
            >
              <option value="ACTIVO">Activo</option>
              <option value="PASIVO">Pasivo</option>
              <option value="CAPITAL">Capital</option>
              <option value="INGRESO">Ingreso</option>
              <option value="COSTO">Costo de Venta</option>
              <option value="GASTO">Gasto</option>
              <option value="RESULTADO_FINANCIAMIENTO">Resultado integral de financiamiento</option>
              <option value="CUENTAS_ORDEN">Cuentas de orden</option>
            </Form.Select>
            {errors.account_type && <Form.Control.Feedback type="invalid">{errors.account_type}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Cuenta Padre</Form.Label>
            <Form.Select
              name="parent"
              value={formData.parent || ''}
              onChange={handleChange}
            >
              <option value="">Sin padre (cuenta raíz)</option>
              {availableParents.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.code} - {acc.name}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Dejar vacío para crear una cuenta raíz
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Código SAT</Form.Label>
            <Form.Control
              type="text"
              name="sat_code"
              value={formData.sat_code}
              onChange={handleChange}
              placeholder="Código del catálogo SAT"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Orden</Form.Label>
            <Form.Control
              type="number"
              min="0"
              name="orden"
              value={formData.orden}
              onChange={handleChange}
            />
            <Form.Text className="text-muted">
              Orden de visualización (0 = por defecto)
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>Descripción</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={formData.description}
              onChange={handleChange}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Check
            type="checkbox"
            name="is_active"
            label="Cuenta Activa"
            checked={formData.is_active}
            onChange={handleChange}
          />
        </Col>
        <Col md={6}>
          <Form.Check
            type="checkbox"
            name="is_deductible"
            label="Cuenta Deducible"
            checked={formData.is_deductible}
            onChange={handleChange}
          />
        </Col>
      </Row>

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading ? 'Guardando...' : (account?.id ? 'Actualizar' : 'Crear')}
        </Button>
      </div>
    </Form>
  )
}

export default AccountForm

